import { queryKeys as analyticsQueryKeys } from "@/api/analytics/queryKeys";
import { useGetExchangeRates } from "@/api/analytics/useGetExchangeRates";
import coreQueryKeys from "@/api/core/hooks/queryKeys";
import useGetUsersByTenantID from "@/api/core/hooks/useGetUsersByTenantID";
import { useActivityTracker } from "@/context/ActivityTrackerProvider";
import { queryKeys as adminQueryKeys } from "@/features/global-admin/hooks/queryKeys";
import { useGetTenantEventsByTenantID } from "@/features/global-admin/hooks/useGetTenantEvents";
import { queryKeys as featureQueryKeys } from "@/hooks/queryKeys";
import useAuthenticatedUser from "@/hooks/useAuthenticatedUser";
import useGatekeeper from "@/hooks/useGatekeeper";
import useGetTenantByID from "@/hooks/useGetTenantByID";
import useUpdateTenant from "@/hooks/useUpdateTenant";
import { useMspStore } from "@/lib/zustand";
import Select from "@/ui-lib/components/Select";
import TextInput from "@/ui-lib/components/TextInput";
import { AlertType, postAlert } from "@/utils/alerts";
import getMergeState from "@/utils/getMergeState";
import { useTheme } from "@emotion/react";
import { faFolderArrowDown, faSearch } from "@fortawesome/pro-light-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import { AnalyticsExchangeRate } from "@ternary/api-lib/analytics/api/AnalyticsApiClient";
import {
  getCurrencyDisplayName,
  getExchangeRateDisplay,
} from "@ternary/api-lib/analytics/utils/NumberFormatUtils";
import { CurrencyCode } from "@ternary/api-lib/constants/enums";
import {
  TenantEventEntity,
  TenantType,
} from "@ternary/api-lib/core/types/Tenant";
import { actions } from "@ternary/api-lib/telemetry";
import Box from "@ternary/api-lib/ui-lib/components/Box";
import Button from "@ternary/api-lib/ui-lib/components/Button";
import Flex from "@ternary/api-lib/ui-lib/components/Flex";
import Icon from "@ternary/api-lib/ui-lib/components/Icon";
import Text from "@ternary/web-ui-lib/components/Text";
import { format } from "date-fns-tz";
import { keyBy } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { useParams } from "react-router-dom";
import copyText from "../copyText";
import CurrencyAuditLogsTable from "./CurrencyAuditLogsTable";

const queryKeys = {
  ...adminQueryKeys,
  ...coreQueryKeys,
  ...analyticsQueryKeys,
  ...featureQueryKeys,
};

interface CurrencyManagementState {
  exchangeRate: number;
  isPreferredCurrencyDisabled: boolean;
  preferredCurrency?: CurrencyCode;
  searchText: string;
  tenantType: TenantType;
}

const initialCurrencyManagementState: CurrencyManagementState = {
  searchText: "",
  preferredCurrency: undefined,
  exchangeRate: 1,
  isPreferredCurrencyDisabled: true,
  tenantType: TenantType.MSP_CUSTOMER,
};

//
// CurrencySettingsPanel Component
//

interface CurrencySettingsPanelProps {
  baseCurrency?: CurrencyCode;
  currencyOptions: Array<{ label: string; value: CurrencyCode }>;
  exchangeRate?: AnalyticsExchangeRate;
  preferredCurrency?: CurrencyCode;
  isDisabled: boolean;
  isLoading: boolean;
  onCurrencyChange: (currency: CurrencyCode) => void;
}

function CurrencySettingsPanel({
  baseCurrency = CurrencyCode.USD,
  currencyOptions,
  exchangeRate,
  isDisabled,
  isLoading,
  preferredCurrency,
  onCurrencyChange,
}: CurrencySettingsPanelProps): JSX.Element {
  const theme = useTheme();

  const exchangeRateDisplay = useMemo(() => {
    if (isLoading || !exchangeRate) {
      return "--";
    }

    const exchangeRateValue =
      baseCurrency === preferredCurrency
        ? 1
        : (exchangeRate.quotes.find(
            (q) => q.quoteCurrency === preferredCurrency
          )?.rate ?? 1);

    return getExchangeRateDisplay(
      baseCurrency,
      exchangeRateValue,
      preferredCurrency
    );
  }, [baseCurrency, exchangeRate, preferredCurrency, isLoading]);

  return (
    <Box
      backgroundColor={theme.panel_backgroundColor}
      borderRadius={theme.borderRadius_2}
      paddingVertical={theme.space_md}
    >
      <Flex direction="column" gap="18px">
        <Flex gap={theme.space_xs} paddingHorizontal={theme.space_md}>
          <Flex direction="column" gap="6px" flex={1}>
            <Text>{copyText.preferredCurrencyLabel}</Text>
            <Select
              placeholder="--"
              value={
                preferredCurrency
                  ? {
                      label: getCurrencyDisplayName(preferredCurrency),
                      value: preferredCurrency,
                    }
                  : undefined
              }
              options={currencyOptions}
              onChange={(option) => option && onCurrencyChange(option.value)}
              isDisabled={isDisabled || isLoading}
              isLoading={isLoading}
            />
          </Flex>
          <Flex direction="column" gap="6px">
            <Text>{copyText.exchangeRateLabel}</Text>
            <TextInput disabled placeholder="--" value={exchangeRateDisplay} />
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
}

//
// AuditLogsSection Component
//

interface AuditLogsSectionProps {
  searchText: string;
  onSearchChange: (text: string) => void;
  csvData: {
    headers: Array<{ label: string; key: string }>;
    rows: Array<Record<string, string>>;
  };
  tenantEvents: TenantEventEntity[];
  isLoading: boolean;
}

function AuditLogsSection({
  isLoading,
  csvData,
  searchText,
  tenantEvents,
  onSearchChange,
}: AuditLogsSectionProps): JSX.Element {
  const theme = useTheme();

  return (
    <Flex direction="column" gap={theme.space_md}>
      {/* Header */}
      <Flex alignItems="center">
        <Flex flex={1}>
          <Text fontSize={theme.h4_fontSize}>{copyText.auditLogLabel}</Text>
        </Flex>
        <Flex alignItems="center" width={200}>
          <TextInput
            iconStart={
              <Icon color={theme.text_color_secondary} icon={faSearch} />
            }
            height={40}
            placeholder={copyText.searchInputPlaceholder}
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </Flex>
        <CSVLink
          data={csvData.rows}
          headers={csvData.headers}
          filename={`currencyChanges-${format(new Date(), "MM-dd-yyyy")}`}
        >
          <Button
            iconEnd={<Icon icon={faFolderArrowDown} />}
            marginLeft={theme.space_xs}
            size="medium"
          />
        </CSVLink>
      </Flex>

      {/* Table */}
      <CurrencyAuditLogsTable
        tenantEvents={tenantEvents}
        isLoading={isLoading}
      />
    </Flex>
  );
}

//
// Main Container Component
//

export default function CurrencyManagementContainer(): JSX.Element {
  const activityTracker = useActivityTracker();
  const authenticatedUser = useAuthenticatedUser();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const gatekeeper = useGatekeeper();
  const mspStore = useMspStore();
  const { tenantID } = useParams();

  // If coming from internal page there will be a tenantID in the query params.
  // Otherwise use the tenantID on authenticatedUser.
  const actingTenantID = tenantID ? tenantID : authenticatedUser.tenant.id;

  //
  // State
  //

  const [state, setState] = useState(initialCurrencyManagementState);
  const mergeState = getMergeState(setState);

  //
  // Queries
  //

  const { data: tenant, isLoading: isLoadingTenant } = useGetTenantByID(
    actingTenantID,
    {
      enabled:
        (gatekeeper.canReadTenantsSystem || gatekeeper.canReadTenantsPartner) &&
        !!actingTenantID,
    }
  );

  const { data: tenantEvents = [], isLoading: isLoadingEvents } =
    useGetTenantEventsByTenantID(actingTenantID, {
      enabled: !!actingTenantID,
    });

  const { data: users = [], isLoading: isLoadingUsers } = useGetUsersByTenantID(
    actingTenantID,
    {
      enabled: !!actingTenantID && gatekeeper.canListUsers,
    }
  );

  const usersKeyedByID = useMemo(() => {
    return keyBy(users, "id");
  }, [users]);

  const currencyOptions = useMemo(() => {
    const currencies = Object.values(CurrencyCode);
    return currencies.map((currency) => ({
      label: getCurrencyDisplayName(currency),
      value: currency,
    }));
  }, []);

  const { data: exchangeRate, isLoading: isLoadingExchangeRate } =
    useGetExchangeRates({
      tenantID: actingTenantID,
      baseCurrencyCode: CurrencyCode.USD,
      queryOptions: {
        enabled: !!authenticatedUser && !!gatekeeper?.canAccessMultiCurrency,
      },
      register: true,
    });

  const hasChanges = useMemo(() => {
    if (!tenant) return false;
    return state.preferredCurrency !== tenant.preferredCurrencyCode;
  }, [tenant, state.preferredCurrency]);

  const isLoadingTenantEvents =
    isLoadingTenant || isLoadingEvents || isLoadingUsers;

  //
  // Audit Logs
  //

  // Filter events that changed preferred currency by email
  const filteredEvents = useMemo(() => {
    const currencyEvents = tenantEvents
      .filter((event) => event.data.preferredCurrencyCode)
      .map((event) => {
        return {
          ...event,
          data: {
            ...event.data,
            updatedBy:
              usersKeyedByID[event.invokerID]?.email ?? event.invokerID,
          },
        };
      });

    if (state.searchText.length > 0) {
      const searchStr = state.searchText.toLowerCase();
      return currencyEvents.filter((event) => {
        const email = event.data.updatedBy?.toLowerCase();
        return email?.includes(searchStr);
      });
    }

    return currencyEvents;
  }, [tenantEvents, state.searchText, usersKeyedByID]);

  // Prepare CSV data for export
  const csvData = useMemo(() => {
    const headers = [
      { label: "Changed By", key: "changedBy" },
      { label: "Date", key: "date" },
      { label: "Currency Code", key: "currencyCode" },
    ];

    const rows = filteredEvents.map((event: TenantEventEntity) => ({
      changedBy: event.data.updatedBy ?? "",
      currencyCode: event.data.preferredCurrencyCode ?? "",
      date: event.createdAt
        ? format(new Date(event.createdAt), "MM/dd/yyyy")
        : "",
    }));

    return { headers, rows };
  }, [filteredEvents]);

  //
  // Mutations
  //

  const { mutate: updateTenant, isPending } = useUpdateTenant({
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.authenticatedUser });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantEvents(actingTenantID),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenants(actingTenantID),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.recommendations(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.analyticsExchangeRates(
          actingTenantID,
          CurrencyCode.USD
        ),
      });

      // Show success toast notification
      postAlert({
        message: copyText.successUpdatingPreferredCurrencyMessage,
        type: AlertType.SUCCESS,
      });
    },
    onError: () => {
      // Show error toast notification
      postAlert({
        message: copyText.errorUpdatingPreferredCurrencyMessage,
        type: AlertType.ERROR,
      });
    },
  });

  //
  // Interaction Handlers
  //

  const handleCurrencyChange = (currency: CurrencyCode) => {
    if (!tenant) return;

    if (state.preferredCurrency !== currency) {
      activityTracker.captureAction(actions.SELECT_ADMIN_CURRENCY_CHANGE, {
        current: currency,
        previous: state.preferredCurrency,
      });
    }

    mergeState({ preferredCurrency: currency });
  };

  const handleSubmit = () => {
    if (!tenant) return;

    updateTenant({
      tenantID: actingTenantID,
      preferredCurrencyCode: state.preferredCurrency,
    });
  };

  //
  // Side Effects
  //

  useEffect(() => {
    if (!tenant) return;

    let isPreferredCurrencyDisabled = false;

    if (tenant.type === TenantType.MSP_CUSTOMER) {
      isPreferredCurrencyDisabled = !authenticatedUser.parentTenantID;
    }

    mergeState({
      tenantType: tenant.type,
      preferredCurrency: tenant.preferredCurrencyCode || CurrencyCode.USD,
      isPreferredCurrencyDisabled,
    });
  }, [tenant, authenticatedUser.tenant.type, mspStore.selectedParentTenantID]);

  //
  // Render
  //

  return (
    <Flex direction="column" gap={theme.space_lg} maxWidth={800}>
      <Flex direction="column" gap={theme.space_xs}>
        <Text fontSize={theme.h4_fontSize}>{copyText.currencyTabLabel}</Text>
        <Text
          fontWeight={theme.fontWeight_thin}
          color={theme.text_color_caption}
        >
          {state.isPreferredCurrencyDisabled &&
          state.tenantType === TenantType.MSP_CUSTOMER
            ? copyText.currencyTabCaptionDisabled
            : copyText.currencyTabCaption}
        </Text>
      </Flex>

      <CurrencySettingsPanel
        preferredCurrency={state.preferredCurrency}
        exchangeRate={exchangeRate}
        isDisabled={state.isPreferredCurrencyDisabled || isLoadingExchangeRate}
        isLoading={isLoadingExchangeRate}
        currencyOptions={currencyOptions}
        onCurrencyChange={handleCurrencyChange}
      />

      <AuditLogsSection
        isLoading={isLoadingTenantEvents}
        csvData={csvData}
        searchText={state.searchText}
        tenantEvents={filteredEvents}
        onSearchChange={(text) => mergeState({ searchText: text })}
      />

      <Flex justifyContent="flex-end" gap={theme.space_xs}>
        <Button disabled={isPending || !hasChanges}>
          {copyText.cancelButtonLabel}
        </Button>
        <Button
          type="button"
          primary
          disabled={isPending || !hasChanges}
          onClick={handleSubmit}
        >
          {copyText.submitButtonLabel}
        </Button>
      </Flex>
    </Flex>
  );
}
